// PRE RMA — Import d'un fichier Excel d'Inventaire / Pré-inventaire : rapprochement des lignes lues avec la liste des médicaments
// et les intrants du CSB, puis plan d'écriture (fonctions pures). Le fichier est lu côté serveur (backend/src/export/xlsxImport.ts) ;
// l'application applique ce plan avec ses écritures habituelles (donc aussi bien en ligne que hors-ligne pour la file de synchro).
import { PRE_PROGRAMMES, validerLigneInventaire, validerPreLigne, validerMedicament } from './inventaire.ts';
import type { LigneInventaire, MedicamentCatalogue, PreLigne } from './inventaire.ts';

export const normaliserNom = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const r2 = (x: number) => Math.round(x * 100) / 100;
const nb = (x: number | null | undefined): number | null => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? r2(x) : null);

export interface LigneInvImportee { nom: string; pu: number | null; debut: number | null; entree: number | null; perime: number | null; fin: number | null; exp: string }
export interface InventaireImporte { mois: number | null; annee: number | null; lignes: LigneInvImportee[]; depense: number | null }

export interface PlanImportInventaire {
  /** Lignes INV_LIGNE à écrire (clé = id du médicament). */
  lignes: Array<{ id: string; donnees: Partial<LigneInventaire> }>;
  /** Médicaments du fichier absents de la liste du CSB : à ajouter à la liste (avec l'id déjà attribué dans `lignes`). */
  nouveaux: MedicamentCatalogue[];
  depense: number | null;
  /** Lignes ignorées (vides) ou refusées (valeurs invalides) — pour l'afficher à l'utilisateur. */
  vides: number;
  refusees: string[];
}

/**
 * @param existants  saisies INV_LIGNE déjà présentes (l'observation et le PU figé sont conservés si le fichier n'en donne pas)
 * @param genererId  fabrique d'identifiant pour un médicament nouveau
 */
export function planifierImportInventaire(
  imp: Pick<InventaireImporte, 'lignes' | 'depense'>, catalogue: MedicamentCatalogue[], existants: Map<string, Partial<LigneInventaire>>, genererId: () => string,
): PlanImportInventaire {
  const parNom = new Map(catalogue.map((m) => [normaliserNom(m.nom), m]));
  const plan: PlanImportInventaire = { lignes: [], nouveaux: [], depense: nb(imp.depense), vides: 0, refusees: [] };
  const vus = new Set<string>();
  for (const l of imp.lignes) {
    const cle = normaliserNom(l.nom);
    if (!cle || vus.has(cle)) continue;
    vus.add(cle);
    const debut = nb(l.debut), entree = nb(l.entree), perime = nb(l.perime), fin = nb(l.fin), pu = nb(l.pu);
    const aDesDonnees = (debut ?? 0) > 0 || (entree ?? 0) > 0 || (perime ?? 0) > 0 || fin !== null || !!l.exp;
    if (!aDesDonnees) { plan.vides += 1; continue; }
    let med = parNom.get(cle);
    if (!med) {
      med = { id: genererId(), nom: l.nom.trim().slice(0, 120), pu: pu ?? 0, traceur: false };
      if (!validerMedicament(med).ok) { plan.refusees.push(`${l.nom} : désignation ou PU invalide.`); continue; }
      plan.nouveaux.push(med); parNom.set(cle, med);
    }
    const base = existants.get(med.id) ?? {};
    const donnees: Partial<LigneInventaire> = {
      ...base, pu: pu ?? base.pu ?? med.pu, debut, entree, perime,
      detail: fin, gros: null, // le modèle papier ne donne que le stock fin total : il est repris en « détail »
      exp: l.exp || base.exp || '', obs: base.obs ?? '',
    };
    const v = validerLigneInventaire(donnees);
    if (!v.ok) { plan.refusees.push(`${l.nom} : ${v.erreurs[0]}`); continue; }
    plan.lignes.push({ id: med.id, donnees });
  }
  return plan;
}

export interface SectionPreImportee { code: string | null; titre: string; items: Array<{ nom: string; detail: number | null; gros: number | null; obs: string }> }

export interface PlanImportPre {
  /** FANOME : mise à jour de INV_LIGNE (détail, gros, observation) pour des médicaments connus. */
  inv: Array<{ id: string; donnees: Partial<LigneInventaire> }>;
  /** Programmes : lignes PRE_LIGNE (clé = clé du produit). */
  pre: Array<{ cle: string; donnees: PreLigne }>;
  nonReconnus: string[];
  refusees: string[];
}

export function planifierImportPre(
  sections: SectionPreImportee[],
  ctx: { catalogue: MedicamentCatalogue[]; intrantsAjoutes: Array<{ cle: string; programme: string; nom: string }>; invExistants: Map<string, Partial<LigneInventaire>> },
): PlanImportPre {
  const plan: PlanImportPre = { inv: [], pre: [], nonReconnus: [], refusees: [] };
  const meds = new Map(ctx.catalogue.map((m) => [normaliserNom(m.nom), m]));
  for (const s of sections) {
    if (!s.code) { if (s.items.some((i) => i.detail !== null || i.gros !== null)) plan.nonReconnus.push(`Section « ${s.titre} » non reconnue`); continue; }
    const prog = PRE_PROGRAMMES.find((p) => p.code === s.code);
    const produits = new Map<string, string>();
    if (prog) {
      prog.produits.forEach((nom, i) => produits.set(normaliserNom(nom), `${prog.code}:${i}`));
      ctx.intrantsAjoutes.filter((x) => x.programme === prog.code).forEach((x) => produits.set(normaliserNom(x.nom), x.cle));
    }
    for (const it of s.items) {
      const detail = nb(it.detail), gros = nb(it.gros), obs = (it.obs ?? '').slice(0, 300);
      if (detail === null && gros === null && !obs) continue;
      if (s.code === 'FANOME') {
        const m = meds.get(normaliserNom(it.nom));
        if (!m) { plan.nonReconnus.push(`${it.nom} (FANOME)`); continue; }
        const base = ctx.invExistants.get(m.id) ?? {};
        const donnees: Partial<LigneInventaire> = { ...base, pu: base.pu ?? m.pu, detail, gros, obs: obs || base.obs || '' };
        const v = validerLigneInventaire(donnees);
        if (!v.ok) plan.refusees.push(`${it.nom} : ${v.erreurs[0]}`); else plan.inv.push({ id: m.id, donnees });
      } else {
        const cle = produits.get(normaliserNom(it.nom));
        if (!cle) { plan.nonReconnus.push(`${it.nom} (${prog?.nom ?? s.titre})`); continue; }
        const donnees: PreLigne = { detail, gros, obs };
        const v = validerPreLigne(donnees);
        if (!v.ok) plan.refusees.push(`${it.nom} : ${v.erreurs[0]}`); else plan.pre.push({ cle, donnees });
      }
    }
  }
  return plan;
}
